import React from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Badge,
  Button,
  UncontrolledTooltip,
} from "reactstrap";
import VisibilityIcon from "@mui/icons-material/Visibility";
import helpers from "utils/helpers";
import { useTranslation } from "context/TranslationProvider";
import { useNavigate } from "react-router-dom";

const getAuthorName = (userObj) => {
  if (!userObj) return "";
  if (typeof userObj === "string") {
    const trimmed = userObj.trim();
    if (!trimmed || /^[0-9a-fA-F]{24}$/.test(trimmed)) return "";
    return trimmed;
  }
  const name = `${userObj.firstName || ""} ${userObj.lastName || ""}`.trim();
  return name || userObj.name || userObj.email || "";
};

const DisputeDetailsCard = ({ dispute }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!dispute) return null;



  const statusKey = dispute.status || "unknown";
  const statusColor = helpers.getStatusClass(statusKey, "ticket").replace("status-", "");
  const formattedStatus = t(`page.support-tickets.status.${statusKey}`, statusKey.replace(/_/g, " "));

  const linkedOrder = dispute?.orderid || dispute?.orderDetails || dispute?.resolvedOrder;

  const getPartyUser = (listingUser, orderUser, roleName) => {
    if (listingUser && typeof listingUser === "object" && (listingUser.firstName || listingUser.email)) {
      return listingUser;
    }
    if (orderUser && typeof orderUser === "object" && (orderUser.firstName || orderUser.email)) {
      return orderUser;
    }
    if (dispute.role === roleName) {
      if (dispute.user && typeof dispute.user === "object" && (dispute.user.firstName || dispute.user.email)) {
        return dispute.user;
      }
      if (dispute.userId && typeof dispute.userId === "object" && (dispute.userId.firstName || dispute.userId.email)) {
        return dispute.userId;
      }
    }
    return null;
  };

  const traveler = getPartyUser(linkedOrder?.traveler_listing_id?.userId, linkedOrder?.travelerId, "Traveler");
  const sender = getPartyUser(linkedOrder?.sender_listing_id?.userId, linkedOrder?.senderId, "Sender");

  const resolutionAuthor =
    getAuthorName(dispute.resolvedBy) ||
    getAuthorName(dispute.updatedBy) ||
    getAuthorName(dispute.assignedTo) ||
    getAuthorName(dispute.user) ||
    getAuthorName(dispute.userId);

  const reopenAuthor =
    getAuthorName(dispute.reopenedBy) ||
    getAuthorName(dispute.updatedBy) ||
    getAuthorName(dispute.assignedTo) ||
    getAuthorName(dispute.user) ||
    getAuthorName(dispute.userId);

  return (
  <>
    <Card className="shadow-md">
      <CardBody>
        <Row className="mb-3 align-items-center">
          <Col>
            <span className="text-muted">
              {t("page.messages.disputeLabel", "Dispute Ticket")}
            </span>
          </Col>
          <Button
            id={`view-dispute-${dispute._id}`}
            size="sm"
            color="link"
            className="mr-3 btn-secondary"
             onClick={() => navigate(`/admin/support-tickets/${dispute._id}/details?source=Dispute`)}
          >
            <VisibilityIcon fontSize="small" />
          </Button>

          <UncontrolledTooltip target={`view-dispute-${dispute._id}`}>
            {t("page.messages.view", "View Details")}
          </UncontrolledTooltip>
        </Row>

        <Row className="mb-2">
          <Col>
            <h5>{dispute.title || "N/A"}</h5>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col>
            <Badge color={statusColor} pill className="text-capitalize">
              {formattedStatus}
            </Badge>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col>
            <small className="text-muted mb-1 d-block">{t("page.messages.issue", "Issue Description")}</small>
            <div>
              {dispute.description}
            </div>
          </Col>
        </Row>

        {dispute.status !== "resolved" && dispute.reopenReason && (
          <Row className="mb-3">
            <Col>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">{t("page.support-tickets.reopenReason", "Reopen Reason")}</small>
                {reopenAuthor && <small className="text-muted font-weight-bold">{t("page.messages.by", "by")} {reopenAuthor}</small>}
              </div>
              <div className="p-2 rounded border text-dark text-sm">
                {dispute.reopenReason}
              </div>
            </Col>
          </Row>
        )}

        {dispute.status === "resolved" && (dispute.resolutionNotes || dispute.resolution) && (
          <Row className="mb-3">
            <Col>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">{t("page.support-tickets.resolutionNotes", "Resolution Notes")}</small>
                {resolutionAuthor && <small className="text-muted font-weight-bold">{t("page.messages.by", "by")} {resolutionAuthor}</small>}
              </div>
              <div className="p-2 rounded border text-dark text-sm">
                {dispute.resolutionNotes || dispute.resolution}
              </div>
            </Col>
          </Row>
        )}

        <Row className="mb-3">
          <Col>
            <small className="text-muted mb-1">{t("page.messages.order.title", "Order Details")}</small>
            <span className="d-block">
              {dispute?.orderid?.orderid}
            </span>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col>
            <small className="text-muted mb-1">{t("page.messages.traveler", "Traveler")}</small>
            <span className="d-block">
              {traveler?.firstName} {traveler?.lastName}
            </span>
            {traveler?.email && (
              <small className="text-muted">{traveler.email}</small>
            )}
          </Col>
        </Row>

        <Row className="mb-3">
          <Col>
            <small className="text-muted mb-1">{t("page.messages.sender", "Sender")}</small>
            <span className="d-block">
              {sender?.firstName} {sender?.lastName}
            </span>
            {sender?.email && (
              <small className="text-muted">{sender.email}</small>
            )}
          </Col>
        </Row>
        {dispute.assignedTo && (
          <Row>
            <Col>
              <small className="text-muted mb-1">{t("page.messages.support.assignedTo", "Assigned To")}</small>
              <span className="d-block">
                {dispute.assignedTo.firstName} {dispute.assignedTo.lastName}
              </span>
              {dispute.assignedTo.email && (
                <small className="text-muted">
                  {dispute.assignedTo.email}
                </small>
              )}
            </Col>
          </Row>
        )}
      </CardBody>
    </Card>

    </>
  );
};

export default DisputeDetailsCard;
